/**
 * When a waiting service worker may take over (docs/mockups/install.html, Spec panel 6).
 * Never under an open page: a new build waits for the next launch. A home-screen app is
 * rarely truly closed, so a resume counts as a launch: visible again after 6+ hours
 * hidden, or on a new local date; on Today, never in a game; and only if a worker is
 * waiting. Then the page posts SKIP_WAITING and reloads on `controllerchange`, which
 * looks like the app opening, because it is. Pure, tested in update.test.ts.
 */

/** Hidden this long, coming back counts as a launch. */
export const RESUME_AFTER_MS = 6 * 60 * 60 * 1000;

export interface ResumeContext {
	/** When the page went hidden (epoch ms), or null if it hasn't been. */
	hiddenAt: number | null;
	/** The local date when it went hidden. */
	hiddenDay: string | null;
	now: number;
	/** The local date now. */
	today: string;
}

/** Coming back to the page now is a "launch": long enough away, or a new day. */
export function isLaunchLikeResume(ctx: ResumeContext): boolean {
	if (ctx.hiddenAt === null) return false;
	if (ctx.hiddenDay !== null && ctx.hiddenDay !== ctx.today) return true;
	return ctx.now - ctx.hiddenAt >= RESUME_AFTER_MS;
}

export interface TakeOverContext extends ResumeContext {
	/** The route's name (router.tsx): only `today` qualifies. */
	route: string;
	/** A new worker is installed and waiting. */
	waiting: boolean;
}

/** Post SKIP_WAITING (and reload on controllerchange) now? */
export function shouldTakeOver(ctx: TakeOverContext): boolean {
	return ctx.waiting && ctx.route === 'today' && isLaunchLikeResume(ctx);
}

/**
 * A failed lazy chunk (`vite:preloadError`, e.g. a page from before a deploy asking for a
 * chunk that's gone) reloads the page once. The guard is a sessionStorage timestamp, so
 * it can never loop; a much later deploy in the same tab may reload once more.
 */
export const PRELOAD_RELOAD_KEY = 'arrow:v1:sw:preload-reloaded';
export const PRELOAD_RELOAD_GAP_MS = 10 * 60 * 1000;

export function shouldReloadAfterPreloadError(lastReloadAt: number | null, now: number): boolean {
	return lastReloadAt === null || !Number.isFinite(lastReloadAt) || now - lastReloadAt > PRELOAD_RELOAD_GAP_MS;
}
